import { httpCodes } from '@shared/constants/httpCodes'
import BadRequestError from '@shared/modules/http/domain/exceptions/BadRequestError'
import ForbiddenError from '@shared/modules/http/domain/exceptions/ForbiddenError'
import UnauthorizedError from '@shared/modules/http/domain/exceptions/UnauthorizedError'

import BaseHTTPError from '../exceptions/BaseHTTPError'
import InternalServerError from '../exceptions/InternalServerError'
import TooManyRequestsError from '../exceptions/TooManyRequestsError'

async function getErrorMessage(response: Response) {
  try {
    const data =
      response.headers.get('Content-Type') === 'application/json'
        ? await response.json()
        : await response.text()
    return data
  } catch (err) {
    return err
  }
}

export default async function HTTPExceptionInterceptor(
  response: Response
): Promise<Response | never> {
  if (response.status >= 400) {
    switch (response.status) {
      case httpCodes.INTERNAL_ERROR:
        throw new InternalServerError({
          message: 'Server could not process the request',
          action: 'Check the logs to see what to do',
          data: await getErrorMessage(response)
        })
      case httpCodes.BAD_REQUEST:
        throw new BadRequestError({
          message: 'User cannot perform activity',
          action: 'Check the logs to see what to do',
          data: await getErrorMessage(response)
        })
      case httpCodes.UNAUTHORIZED:
        throw new UnauthorizedError({
          message: 'User not authenticated',
          action: 'Log in with a valid user',
          data: await getErrorMessage(response)
        })
      case httpCodes.FORBIDDEN:
        throw new ForbiddenError({
          message: 'User cannot perform this action',
          action: 'Change to an user with such permission',
          data: await getErrorMessage(response)
        })
      case httpCodes.TOO_MANY_REQUESTS:
        throw new TooManyRequestsError({
          message: 'Too many requests',
          action:
            'You have exceeded the maximum number of requests allowed. Please try again later.',
          data: await getErrorMessage(response)
        })
      default:
        throw new BaseHTTPError({
          name: TooManyRequestsError.name,
          httpCode: httpCodes.TOO_MANY_REQUESTS,
          message: 'Something went wrong',
          action:
            'Unrecognized exception, it is recommended that you contact support.',
          data: await getErrorMessage(response)
        })
    }
  }

  return response
}
